import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef
} from "react";
import {
    drawGrain,
    drawVignette,
    drawWash
} from "../utils/canvasEffects";
import { getFilteredBitmap } from "../utils/filterCache";
import {
    clampStickerCenter,
    drawSelection,
    drawSticker,
    hitTestSticker
} from "../utils/stickerDraw";

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

// Dải ảnh: vẽ canvas và xử lý kéo thả.
//
// Dùng Pointer Events chứ không phải Mouse Events, nhờ vậy chạy được trên màn
// hình cảm ứng. `setPointerCapture` cũng lo luôn trường hợp nhả tay ra ngoài
// canvas, nên không cần listener window.mouseup vá tạm như trước.
const StripCanvas = forwardRef(function StripCanvas(
    {
        frame,
        frameImg,
        photos,
        stickers,
        selectedStickerId,
        filter,
        mode,
        onMovePhoto,
        onMoveSticker,
        onSelectSticker
    },
    ref
) {
    const canvasRef = useRef(null);
    // Thao tác kéo đang diễn ra. Để trong ref chứ không phải state: kéo là việc
    // xảy ra liên tục, cho vào state chỉ tốn thêm một lượt render mỗi lần chạm.
    const dragRef = useRef(null);

    const render = useCallback(
        (opts = {}) => {
            const canvas = canvasRef.current;
            if (!canvas || !frameImg || !frame) return;

            const ctx = canvas.getContext("2d");
            canvas.width = frameImg.width;
            canvas.height = frameImg.height;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            photos.forEach(p => {
                const slot = frame.slots[p.slotIndex];
                if (!slot) return;

                const bitmap = getFilteredBitmap(p, filter);

                ctx.save();
                ctx.beginPath();
                ctx.rect(slot.x, slot.y, slot.w, slot.h);
                ctx.clip();
                ctx.drawImage(
                    bitmap,
                    slot.x + p.offsetX,
                    slot.y + p.offsetY,
                    bitmap.width * p.scale,
                    bitmap.height * p.scale
                );

                if (filter.wash) drawWash(ctx, slot, filter.wash);
                if (filter.grain) drawGrain(ctx, slot, filter.grain);
                if (filter.vignette) drawVignette(ctx, slot, filter.vignette);
                ctx.restore();
            });

            // Khung đè lên trên, che phần ảnh thừa ngoài lỗ (kể cả lỗ trái tim).
            ctx.drawImage(frameImg, 0, 0, frameImg.width, frameImg.height);

            stickers.forEach(s => drawSticker(ctx, s));

            if (!opts.forExport && selectedStickerId != null) {
                const s = stickers.find(item => item.id === selectedStickerId);
                if (s) drawSelection(ctx, s);
            }
        },
        [frame, frameImg, photos, stickers, selectedStickerId, filter]
    );

    useEffect(() => {
        render();
    }, [render]);

    useImperativeHandle(
        ref,
        () => ({
            // Vẽ lại không có viền chọn -> lấy ảnh -> vẽ lại như cũ.
            toPNG() {
                const canvas = canvasRef.current;
                if (!canvas) return null;

                render({ forExport: true });
                const url = canvas.toDataURL("image/png");
                render();
                return url;
            },

            // Trả về Blob PNG. Dùng Blob thay cho data URL để tải/chia sẻ được
            // trên điện thoại: iOS Safari hay bỏ qua thẻ <a download> khi href là
            // một data URL dài, còn Blob (qua Web Share hoặc object URL) thì chạy.
            toBlob() {
                const canvas = canvasRef.current;
                if (!canvas) return Promise.resolve(null);

                render({ forExport: true });
                return new Promise(resolve => {
                    canvas.toBlob(blob => {
                        // Khôi phục lại viền chọn sau khi đã lấy xong ảnh.
                        render();
                        resolve(blob);
                    }, "image/png");
                });
            }
        }),
        [render]
    );

    // Toạ độ chuột/ngón tay đổi sang hệ toạ độ canvas gốc (1200×3000).
    const getCoords = e => {
        const canvas = canvasRef.current;
        const r = canvas.getBoundingClientRect();
        return {
            x: (e.clientX - r.left) * (canvas.width / r.width),
            y: (e.clientY - r.top) * (canvas.height / r.height)
        };
    };

    const handlePointerDown = e => {
        const canvas = canvasRef.current;
        if (!canvas || !frame) return;

        const { x, y } = getCoords(e);

        if (mode === "photo") {
            for (let i = photos.length - 1; i >= 0; i--) {
                const p = photos[i];
                const slot = frame.slots[p.slotIndex];
                if (!slot) continue;

                if (x >= slot.x && x <= slot.x + slot.w && y >= slot.y && y <= slot.y + slot.h) {
                    dragRef.current = {
                        kind: "photo",
                        id: p.id,
                        dx: x - slot.x - p.offsetX,
                        dy: y - slot.y - p.offsetY
                    };
                    canvas.setPointerCapture(e.pointerId);
                    return;
                }
            }
            return;
        }

        if (mode === "decorate") {
            for (let i = stickers.length - 1; i >= 0; i--) {
                const s = stickers[i];
                if (hitTestSticker(s, x, y)) {
                    dragRef.current = { kind: "sticker", id: s.id, dx: x - s.x, dy: y - s.y };
                    onSelectSticker(s.id);
                    canvas.setPointerCapture(e.pointerId);
                    return;
                }
            }
            onSelectSticker(null);
        }
    };

    const handlePointerMove = e => {
        const drag = dragRef.current;
        if (!drag) return;

        const canvas = canvasRef.current;
        const { x, y } = getCoords(e);

        if (drag.kind === "photo") {
            const p = photos.find(item => item.id === drag.id);
            if (!p) return;

            const slot = frame.slots[p.slotIndex];
            const w = p.img.width * p.scale;
            const h = p.img.height * p.scale;

            // Ảnh luôn phủ kín ô nên biên dưới âm, biên trên bằng 0.
            onMovePhoto(drag.id, {
                offsetX: clamp(x - slot.x - drag.dx, slot.w - w, 0),
                offsetY: clamp(y - slot.y - drag.dy, slot.h - h, 0)
            });
            return;
        }

        onMoveSticker(
            drag.id,
            clampStickerCenter(canvas, x - drag.dx, y - drag.dy)
        );
    };

    const endDrag = e => {
        const canvas = canvasRef.current;
        if (canvas?.hasPointerCapture?.(e.pointerId)) {
            canvas.releasePointerCapture(e.pointerId);
        }
        dragRef.current = null;
    };

    return (
        <canvas
            ref={canvasRef}
            style={canvasStyle}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
        />
    );
});

const canvasStyle = {
    width: 200,
    height: 500,
    borderRadius: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
    // Chặn trình duyệt cuộn/thu phóng trang khi kéo hình dán bằng ngón tay.
    touchAction: "none"
};

export default StripCanvas;
